import axios from 'axios';

const BASE_DECISION_FUNNEL = '/app/decisionFunnel';
const BASE_TWO_LAYERS_RULE_GROUP = '/app/twoLayersRuleGroup';
const BASE_RULE = '/app/rule';

export const getDecisionConfiguration = async (requestDecisionDto) => {
    const params = {
        flowIds: requestDecisionDto?.flowIds?.join(','),
        decisionIds: requestDecisionDto?.decisionIds?.join(','),
        secondaryKey: requestDecisionDto?.secondaryKey,
        secondaryKeyId: requestDecisionDto?.secondaryKeyId,
    };

    const { data } = await axios.post(`${BASE_DECISION_FUNNEL}/decisions/config`, null, { params });

    return data;
};

export const createDecision = async () => {
    const { data } = await axios.post(`${BASE_DECISION_FUNNEL}/decisions`);

    return data;
};

export const importRules = async (params) => {
    const { data } = await axios.get(`${BASE_DECISION_FUNNEL}/decisions/rules?${params}`);

    return data;
};

export const sequenceSaveDefaultOption = async (payload) => {
    await axios.post(`${BASE_DECISION_FUNNEL}/decisions/default-option`, payload);
};

export const decisionTest = async (payload) => {
    const { data } = await axios.post(`${BASE_DECISION_FUNNEL}/decisions/test`, null, {
        params: payload,
    });

    return data;
};

export const decisionEvaluate = async (payload) => {
    const { data } = await axios.post(`${BASE_DECISION_FUNNEL}/decisions/evaluation`, payload, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return data;
};

export const getDebugDataDump = async (params) => {
    const { data } = await axios.get(`${BASE_DECISION_FUNNEL}/dataDump?${params}`);

    return data;
};

export const addRule = async (payload) => {
    const { data } = await axios.post(`${BASE_TWO_LAYERS_RULE_GROUP}/sequences/rules`, payload, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const addRuleGroup = async (payload) => {
    const { data } = await axios.post(`${BASE_TWO_LAYERS_RULE_GROUP}/sequences/rule-groups`, payload, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const deleteRule = async (ruleId) => {
    const { data } = await axios.delete(`${BASE_TWO_LAYERS_RULE_GROUP}/sequences/rules/${ruleId}`);

    return data;
};

export const getChildrenTagFirst = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/children/tag-first?${params}`);

    return data;
};

export const getChildren = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/children?${params}`);

    return data;
};

export const getSubjects = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/subjects?${params}`);

    return data;
};

export const categoryIsField = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/category-is-field?${params}`);

    return data;
};

export const categoryAsField = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/category-as-field?${params}`);

    return data;
};

export const mayAndValues = async (constraint) => {
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/validations/may-and-values?constraint=${constraint}`);

    return data;
};

export const mayOrValues = async (constraint) => {
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/validations/may-or-values?constraint=${constraint}`);

    return data;
};

export const getInputType = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/input-type?${params}`);

    return data;
};

export const getInputAttributes = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/input-attributes?${params}`);

    return data;
};

export const getTestInputType = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/test-input-type?${params}`);

    return data;
};

export const getTestInputAttributes = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/test-input-attributes?${params}`);

    return data;
};

export const getLabel = async (payload) => {
    const params = new URLSearchParams({ ...payload }).toString();
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/label?${params}`);

    return data;
};

export const getAllUsers = async () => {
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/users`);

    return data;
};

export const searchTags = async ({ query = '', limit = 25 } = {}) => {
    const params = new URLSearchParams({ query, limit });
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/tags/search?${params}`);

    return { ...data, map: data?.map || {} };
};

const TAG_LOOKUP_BATCH_SIZE = 100;

let pendingTagLookup = null;

const fetchTagIdChunk = async (ids) => {
    const params = new URLSearchParams({ ids: ids.join(',') });
    const { data } = await axios.get(`${BASE_TWO_LAYERS_RULE_GROUP}/rules/tags?${params}`);

    return data.map || {};
};

const isLookupableTagId = (id) => /^\d+$/.test(id) && Number.isSafeInteger(Number(id));

const pickTags = (map, ids) => ids.reduce((picked, id) => {
    if (map[id] !== undefined) {
        picked[id] = map[id];
    }

    return picked;
}, {});

const flushTagLookup = async ({ ids, waiters }) => {
    const chunks = [];

    for (let i = 0; i < ids.length; i += TAG_LOOKUP_BATCH_SIZE) {
        chunks.push(ids.slice(i, i + TAG_LOOKUP_BATCH_SIZE));
    }

    const settled = await Promise.allSettled(chunks.map(fetchTagIdChunk));

    const map = {};
    const errorsById = new Map();

    settled.forEach((result, i) => {
        if (result.status === 'fulfilled') {
            Object.assign(map, result.value);

            return;
        }

        chunks[i].forEach((id) => errorsById.set(id, result.reason));
    });

    waiters.forEach(({ ids: requested, resolve, reject }) => {
        const failedId = requested.find((id) => errorsById.has(id));

        if (failedId !== undefined) {
            reject(errorsById.get(failedId));

            return;
        }

        resolve({ map: pickTags(map, requested) });
    });
};

export const getTagsByIds = async (ids) => {
    if (!ids?.length) {
        return { map: {} };
    }

    const requested = ids.map(String).filter(isLookupableTagId);

    if (!requested.length) {
        return { map: {} };
    }

    if (!pendingTagLookup) {
        pendingTagLookup = { ids: new Set(), waiters: [] };

        Promise.resolve().then(() => {
            const batch = pendingTagLookup;

            pendingTagLookup = null;

            flushTagLookup({ ids: [...batch.ids], waiters: batch.waiters });
        });
    }

    requested.forEach((id) => pendingTagLookup.ids.add(id));

    return new Promise((resolve, reject) => {
        pendingTagLookup.waiters.push({ ids: requested, resolve, reject });
    });
};

export const duplicateRule = async (ruleId, ruleGroupInnerId, ddKeys) => {
    const { data } = await axios.post(`${BASE_TWO_LAYERS_RULE_GROUP}/sequences/${ruleId}/rules/${ruleGroupInnerId}/duplicate`, ddKeys, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const sequenceImportRules = async (sourceRuleGroupId, targetRuleGroupId, ddKeys) => {
    const { data } = await axios.post(`${BASE_TWO_LAYERS_RULE_GROUP}/sequences/rules/from/${sourceRuleGroupId}/to/${targetRuleGroupId}`, ddKeys, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const sequenceDeleteAllRules = async (ruleGroupId, ddKeys) => {
    const { data } = await axios.post(`${BASE_TWO_LAYERS_RULE_GROUP}/sequences/${ruleGroupId}/all-rules`, ddKeys, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const saveRule = async (payload) => {
    const { data } = await axios.post(`${BASE_RULE}/decisions/rules`, payload, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const saveRuleValue = async (payload) => {
    const { data } = await axios.post(`${BASE_RULE}/decisions/rule-values`, payload, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const deleteRuleValue = async (ruleValueId, ddKeys) => {
    const { data } = await axios.post(`${BASE_RULE}/decisions/${ruleValueId}/rule-values`, ddKeys, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};

export const addRuleValue = async (ruleId, payload) => {
    const { data } = await axios.post(`${BASE_RULE}/decisions/${ruleId}/values`, payload, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return data;
};
